/**
 * CricCam Pro — Automated Live AI Commentary Engine
 * Generates natural language broadcast commentary for every ball.
 */

export const SHOT_DIRECTIONS = Object.freeze([
  { id: 'cover', label: 'Cover / Extra Cover', short: 'Covers' },
  { id: 'midwicket', label: 'Mid-Wicket / Cow Corner', short: 'Mid-Wicket' },
  { id: 'point', label: 'Point / Backward Point', short: 'Point' },
  { id: 'longon', label: 'Long-On', short: 'Long-On' },
  { id: 'longoff', label: 'Long-Off', short: 'Long-Off' },
  { id: 'thirdman', label: 'Third Man / Short Third', short: 'Third Man' },
  { id: 'fineleg', label: 'Fine Leg', short: 'Fine Leg' },
  { id: 'squareleg', label: 'Square Leg / Deep Square', short: 'Square Leg' }
]);

const DOT_TEMPLATES = [
  "{bowler} pitches it full on off-stump, {striker} defends back to the bowler.",
  "{bowler} hurls a good length delivery, {striker} taps it towards cover. No run.",
  "{bowler} bowls outside off, left alone by {striker} through to the keeper.",
  "{bowler} delivers a tight line on middle, {striker} works it to mid-wicket. Dot ball.",
  "Solid defense from {striker} off {bowler}. Dot ball.",
  "{bowler} fires it in quick, {striker} blocks it down the pitch."
];

const SINGLE_TEMPLATES = [
  "{striker} pushes {bowler}'s delivery towards {direction} for a quick single.",
  "{striker} dabs it softly into the off-side and steals a single off {bowler}.",
  "{striker} works {bowler}'s length ball to {direction} and rotates the strike.",
  "{striker} drives {bowler}'s full pitch ball down to {direction} for one run."
];

const TWO_TEMPLATES = [
  "{striker} places it nicely through {direction} off {bowler} and hustles back for two!",
  "Great running! {striker} flicks {bowler} to {direction} and completes a tight couple.",
  "{striker} lofts it over the infield towards {direction}, easy two runs."
];

const THREE_TEMPLATES = [
  "Superb placement! {striker} pierces the gap at {direction} off {bowler}, deep fielder cuts it off. Three runs!"
];

const FOUR_TEMPLATES = [
  { text: "FOUR! {striker} unleashes a glorious shot through {direction} off {bowler}! Right out of the middle!", audio: null },
  { text: "Aakash Chopra style: Darshak bane fielder, fielder bane darshak! {striker} ka behtareen chauka {direction} ki taraf!", audio: "/audio/commentary/four_1.mp3" },
  { text: "Ravi Shastri style: That went like a tracer bullet! Beautiful boundary for {striker} past {direction}!", audio: "/audio/commentary/four_2.mp3" },
  { text: "Sidhu style: Khataak! Chaar run, {striker} ka teer seedha {direction} ki boundary mein!", audio: "/audio/commentary/four_3.mp3" }
];

const SIX_TEMPLATES = [
  { text: "SIX! BANG! {striker} clears his front leg and lofts {bowler} high over {direction} into the stands!", audio: null },
  { text: "Sidhu style: Gagan chumbi chakka! {striker} ne utha ke maara {direction} ke upar se, maano aakash ko choom liya!", audio: "/audio/commentary/six_1.mp3" },
  { text: "Aakash Chopra style: Aur ye kamaal ka prahaar! Ekdum hawa-hawai chakka {direction} ki or!", audio: "/audio/commentary/six_2.mp3" },
  { text: "Ravi Shastri style: He hits that like a tracer bullet into the stands! Huge six over {direction} by {striker}!", audio: "/audio/commentary/six_3.mp3" }
];

const WICKET_BOWLED_TEMPLATES = [
  { text: "OUT! CLEAN BOWLED! {bowler} produces an absolute peach! {striker} has to walk back!", audio: null },
  { text: "Sidhu style: Teeno killiyan bikher di! {bowler} ne stump ko naachne pe majboor kar diya! {striker} OUT!", audio: "/audio/commentary/wicket_1.mp3" },
  { text: "Aakash Chopra style: Seedhi baat no bakwaas, gilli dande ki tarah stump ukhad diye {bowler} ne!", audio: "/audio/commentary/wicket_2.mp3" },
  { text: "Ravi Shastri style: Knocked him over! The off-stump goes for a walk! {striker} is completely bamboozled!", audio: "/audio/commentary/wicket_3.mp3" }
];

const WICKET_CAUGHT_TEMPLATES = [
  "OUT! CAUGHT! {striker} tries to clear {direction} off {bowler}, but gets top-edge and {fielder} takes a clean catch!",
  "OUT! TAKEN! {striker} edges {bowler}'s delivery straight to {fielder}! Big wicket for {bowler}!"
];

const WICKET_LBW_TEMPLATES = [
  "OUT! LBW! {bowler} strikes! Pitches in line, nips back and traps {striker} right in front of the stumps!"
];

const WICKET_RUNOUT_TEMPLATES = [
  "OUT! RUN OUT! Mix-up between the wickets! Direct hit by {fielder} and {striker} is caught short!"
];

const WICKET_STUMPED_TEMPLATES = [
  "OUT! STUMPED! {striker} steps out to {bowler}, misses the line completely, and the keeper whips off the bails in a flash!"
];

const WICKET_GENERIC_TEMPLATES = [
  "OUT! WICKET! {striker} departs! Big breakthrough for {bowler}!"
];

const WIDE_TEMPLATES = [
  "WIDE! {bowler} strays down the leg side, umpire extends his arms.",
  "WIDE! Fired way outside off-stump by {bowler}, extra run added to the total."
];

const NOBALL_TEMPLATES = [
  "NO BALL! {bowler} oversteps the crease! Free Hit coming up for {striker}!",
  "NO BALL! Above waist height full toss from {bowler}. High drama!"
];

const BYE_TEMPLATES = [
  "Beaten! {bowler}'s delivery goes past {striker} and the keeper — the batters steal byes.",
  "Through to the keeper off {bowler}, and they scamper across for byes. Not off the bat."
];

const LEGBYE_TEMPLATES = [
  "Off the pad! {striker} misses the flick off {bowler} and they run leg-byes.",
  "{bowler} hits the pad, ball rolls away to {direction} — leg-byes, no run off the bat."
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Generate broadcast commentary string
 */
export function generateBallCommentary(ball, matchContext = {}) {
  if (!ball || typeof ball !== 'object') {
    return { text: 'Play continues.', audio: null };
  }

  const striker = matchContext.strikerName || ball.strikerName || 'Batsman';
  const bowler = matchContext.bowlerName || ball.bowlerName || 'Bowler';
  const fielder = matchContext.fielderName || ball.fielderName || 'the fielder';
  const rawDirection = ball.shotDirection || matchContext.shotDirection;
  
  let direction = 'the gap';
  if (rawDirection) {
    const found = SHOT_DIRECTIONS.find(d => d.id === rawDirection || d.short === rawDirection);
    direction = found ? found.short : rawDirection;
  }

  // Check Milestone
  if (matchContext.strikerRunsAfter && matchContext.strikerRunsAfter === 50 && ball.runs > 0) {
    const boundaryShot = ball.runs === 6 ? `launches a HUGE SIX over ${direction}` : (ball.runs === 4 ? `smashes a FOUR past ${direction}` : `pushes through ${direction}`);
    return { text: `🎉 FIFTY! Milestone for ${striker}! ${striker} ${boundaryShot} off ${bowler} to reach a glorious 50! What a knock!`, audio: "/audio/commentary/milestone_1.mp3" };
  }
  if (matchContext.strikerRunsAfter && matchContext.strikerRunsAfter === 100 && ball.runs > 0) {
    const boundaryShot = ball.runs === 6 ? `clears the stands with a MONSTROUS SIX` : (ball.runs === 4 ? `drives with supreme control for FOUR` : `steals a run`);
    return { text: `👑 CENTURY! 100 RUNS FOR ${striker.toUpperCase()}! ${striker} ${boundaryShot} off ${bowler}! Magnificent hundred! Stand up and applaud!`, audio: "/audio/commentary/milestone_2.mp3" };
  }

  let templateText = "";
  let audioPath = null;

  // Handle Wickets
  if (ball.type === 'wicket' || ball.wicketType || ball.isWicket) {
    const wType = (ball.wicketType || ball.dismissalType || '').toLowerCase();
    let template = pickRandom(WICKET_GENERIC_TEMPLATES);
    if (wType.includes('bowled')) template = pickRandom(WICKET_BOWLED_TEMPLATES);
    else if (wType.includes('caught') || wType.includes('catch')) template = pickRandom(WICKET_CAUGHT_TEMPLATES);
    else if (wType.includes('lbw')) template = pickRandom(WICKET_LBW_TEMPLATES);
    else if (wType.includes('run')) template = pickRandom(WICKET_RUNOUT_TEMPLATES);
    else if (wType.includes('stump')) template = pickRandom(WICKET_STUMPED_TEMPLATES);
    
    templateText = typeof template === 'object' ? template.text : template;
    audioPath = typeof template === 'object' ? template.audio : null;
  }

  // Handle Extras
  else if (ball.type === 'wide') {
    templateText = pickRandom(WIDE_TEMPLATES);
  }
  else if (ball.type === 'nb') {
    templateText = pickRandom(NOBALL_TEMPLATES);
  }
  // Byes and leg-byes are NOT off the bat — without these they fell through to
  // the run templates and announced shots the striker never played.
  else if (ball.type === 'b') {
    templateText = pickRandom(BYE_TEMPLATES);
  }
  else if (ball.type === 'lb') {
    templateText = pickRandom(LEGBYE_TEMPLATES);
  }
  else {
    // Handle Runs
    const runs = Number(ball.runs || 0);
    let template = pickRandom(DOT_TEMPLATES);
    if (runs === 1) template = pickRandom(SINGLE_TEMPLATES);
    else if (runs === 2) template = pickRandom(TWO_TEMPLATES);
    else if (runs === 3) template = pickRandom(THREE_TEMPLATES);
    else if (runs === 4) template = pickRandom(FOUR_TEMPLATES);
    else if (runs >= 6) template = pickRandom(SIX_TEMPLATES);
    
    templateText = typeof template === 'object' ? template.text : template;
    audioPath = typeof template === 'object' ? template.audio : null;
  }

  // Player names are user data. Passing them as a raw replacement string lets
  const finalString = templateText
    .replace(/{striker}/g, () => String(striker))
    .replace(/{bowler}/g, () => String(bowler))
    .replace(/{fielder}/g, () => String(fielder))
    .replace(/{direction}/g, () => String(direction));

  return { text: finalString, audio: audioPath };
}
